import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import { ChevronLeft, Trophy, ThumbsUp, Check } from 'lucide-react';

interface Option {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
}

const options: Option[] = [
  {
    id: 'yes',
    name: 'Yes, prioritize highly-rated films',
    description: 'Focus on movies with high critic scores and award recognition',
    icon: <Trophy className="h-6 w-6" />
  },
  {
    id: 'no',
    name: 'No, just show movies that match my preferences',
    description: 'Show me movies based on my selected genres, moods, and favorites',
    icon: <ThumbsUp className="h-6 w-6" />
  }
];

interface Props {
  selectedOption: string;
  onSelect: (option: string) => void;
  onBack: () => void;
  showBackButton: boolean;
}

export function CriticallyAcclaimedSelection({ selectedOption: initialSelectedOption, onSelect, onBack, showBackButton }: Props) {
  const [selectedOption, setSelectedOption] = useState<string>(initialSelectedOption);
  const [error, setError] = useState<string | null>(null);

  const handleOptionSelect = (optionId: string) => {
    setSelectedOption(optionId);
    setError(null);
  };

  const handleSubmit = () => {
    if (!selectedOption) {
      setError('Please select an option');
      return;
    }
    onSelect(selectedOption);
  };

  return (
    <div className="space-y-8">
      <div className="space-y-2 text-center">
        <h2 className="display text-2xl font-semibold sm:text-3xl">
          Prioritize the <span className="text-gradient-dusk">critically acclaimed</span>?
        </h2>
        <p className="text-muted-foreground">
          Choose whether to focus on highly-rated films or see all movies that match your preferences.
        </p>
      </div>

      <div className="grid gap-3">
        {options.map((option) => {
          const isSelected = selectedOption === option.id;
          return (
            <button
              key={option.id}
              onClick={() => handleOptionSelect(option.id)}
              className={cn('select-card p-5', isSelected && 'select-card-active')}
              aria-pressed={isSelected}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex gap-4">
                  <div
                    className={cn(
                      'shrink-0 rounded-2xl border border-white/[0.07] bg-white/[0.04] p-3 transition-colors duration-200',
                      isSelected ? 'text-primary' : 'text-muted-foreground'
                    )}
                  >
                    {option.icon}
                  </div>
                  <div>
                    <h3 className={cn('font-semibold', isSelected ? 'text-primary' : 'text-foreground')}>
                      {option.name}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">{option.description}</p>
                  </div>
                </div>
                {isSelected && (
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Check className="h-4 w-4" strokeWidth={3} />
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {error && (
        <p className="text-center text-sm text-red-400">{error}</p>
      )}

      <div className="flex items-center gap-3">
        {showBackButton && (
          <button onClick={onBack} className="btn-ghost px-5 py-3">
            <ChevronLeft className="h-5 w-5" />
            Back
          </button>
        )}
        <button onClick={handleSubmit} className="btn-marquee flex-1 px-4 py-3.5 text-base">
          Next
        </button>
      </div>
    </div>
  );
}